import { getTimezoneOffsetLabel, normalizeTimeZone, timezoneLabel } from '@proaxiv/ui'

// ── Timezone-aware datetime utilities ────────────────────────────────────────
//
// All stored datetimes use ISO strings that include the UTC offset
// (e.g. "2026-06-15T07:32:00+07:00"). The stored value is never mutated.
//
// Two display modes exist:
//   - formatLocalDateTime — converts to the viewer's device timezone. Only
//     for personal action/audit timestamps (registered/accepted/attended at),
//     which are inherently about the viewer's own point in time.
//   - formatEventDateTime — renders in an explicit IANA timezone (the
//     event's own `timezone` field). Every event-schedule field (start/end,
//     ticket activation, registration deadlines) must use this so every
//     viewer sees the same wall-clock time regardless of their own timezone.

/**
 * Returns the current moment as an ISO 8601 string that includes the local
 * UTC offset, e.g. "2026-06-15T07:32:00+07:00".
 *
 * This is used when recording when an invitation was (re-)sent so that the
 * original timezone context is preserved in storage.
 */
export function nowIsoWithOffset(): string {
  const now = new Date()
  const offsetMs = now.getTimezoneOffset() * 60_000
  const sign = offsetMs <= 0 ? '+' : '-'
  const absOffsetMs = Math.abs(offsetMs)
  const hh = String(Math.floor(absOffsetMs / 3_600_000)).padStart(2, '0')
  const mm = String(Math.floor((absOffsetMs % 3_600_000) / 60_000)).padStart(2, '0')
  // Build local ISO without the trailing 'Z'
  const local = new Date(now.getTime() - offsetMs)
  return `${local.toISOString().slice(0, 19)}${sign}${hh}:${mm}`
}

/** Formatters — constructed once per locale, reused for every call with that locale.
 *  Uses explicit field options (not dateStyle/timeStyle) for broader runtime support. */
const localDtFormatterCache = new Map<string, Intl.DateTimeFormat>()

/**
 * Formats an ISO datetime string (with or without offset) for display in the
 * viewer's local timezone.  The result includes the timezone abbreviation so
 * users in different zones see unambiguous times.
 *
 * `locale` should be the app's current UI language (e.g. `i18n.language`) so month/weekday
 * names and date order match whatever the viewer already reads the rest of the page in —
 * defaults to `'en'` only for callers with no i18n context (e.g. this module's own tests).
 *
 * Example output (device in ICT / UTC+7, locale 'en'):
 *   "Jun 15, 2026, 02:32 AM ICT (UTC+07:00)"
 */
export function formatLocalDateTime(iso: string, locale = 'en'): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso // fallback: return raw string
  let formatter = localDtFormatterCache.get(locale)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
    localDtFormatterCache.set(locale, formatter)
  }
  const deviceZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  return `${formatter.format(d)} ${timezoneLabel(deviceZone, d)}`
}

// ── Event-timezone formatting ─────────────────────────────────────────────────

// Formatters are cached per-(timezone, locale, options) triple since Intl.DateTimeFormat
// construction is comparatively expensive.
const eventFormatterCache = new Map<string, Intl.DateTimeFormat>()

/**
 * Formats an ISO datetime string for display in a *specific* IANA timezone —
 * the event's own `timezone` field — rather than the viewer's device timezone.
 *
 * Event start/end times, ticket activation, registration deadlines, and any
 * other "when does this event happen" field must always be shown in the
 * event's timezone: two participants in different timezones viewing the same
 * event should see the same wall-clock time (e.g. "7:00 PM"), not two
 * different times converted to their own locale. Falls back to UTC if no
 * timezone is provided.
 *
 * `locale` should be the app's current UI language (e.g. `i18n.language`) so month/weekday
 * names and date order match whatever the viewer already reads the rest of the page in —
 * only the wall-clock time itself stays fixed to the event's timezone, never the language.
 * Defaults to `'en'` only for callers with no i18n context (e.g. this module's own tests).
 *
 * The timezone suffix matches the edit-form picker label, e.g.
 * `"CEST (UTC+02:00)"`, using the offset at the given instant (DST-aware).
 * Intl short names like `"GMT+2"` are never used.
 *
 * Pass `{ includeTimeZoneName: false }` to omit the suffix (e.g. the public event
 * page shows only the wall-clock time). Defaults to `true` so other callers stay unchanged.
 *
 * Example (event in Europe/Rome, summer, locale 'en'):
 *   "Jun 15, 2026, 02:32 AM CEST (UTC+02:00)"
 * With `{ includeTimeZoneName: false }`:
 *   "Jun 15, 2026, 02:32 AM"
 */
export function formatEventDateTime(
  iso: string,
  timeZone: string | null | undefined,
  locale = 'en',
  options?: Intl.DateTimeFormatOptions,
  { includeTimeZoneName = true }: { includeTimeZoneName?: boolean } = {}
): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso // fallback: return raw string

  // Defensive normalize: a stored event timezone may be an exotic identifier
  // (e.g. Etc/GMT-9 from a misconfigured device) — always render a clean zone.
  const zone = normalizeTimeZone(timeZone || 'UTC')
  // Strip any caller-supplied timeZoneName — we append an IANA + offset label instead.
  const restOptions = { ...(options ?? {}) }
  delete restOptions.timeZoneName
  const cacheKey = `${zone}|${locale}|${JSON.stringify(restOptions)}`
  let formatter = eventFormatterCache.get(cacheKey)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...restOptions,
      timeZone: zone,
    })
    eventFormatterCache.set(cacheKey, formatter)
  }

  if (!includeTimeZoneName) return formatter.format(d)
  const offset = getTimezoneOffsetLabel(zone, d)
  const zoneLabel = offset ? timezoneLabel(zone, d) : zone
  return `${formatter.format(d)} ${zoneLabel}`
}

// ── Time-left helper ───────────────────────────────────────────────────────────

/** Describes how much time is left until an event starts. */
export type TimeLeft =
  | { unit: 'days'; value: number }
  | { unit: 'hours'; value: number }
  | { unit: 'minutes'; value: number }
  | { unit: 'started' }

/**
 * Returns a structured time-left value from `now` until `startIso`.
 *
 * Rules (matching reminder email spec):
 *   diff >= 24 h  →  days   (floor(totalHours / 24))
 *   1h <= diff < 24h  →  hours  (floor(totalMinutes / 60))
 *   0 < diff < 1h  →  minutes (floor(totalSeconds / 60))
 *   diff <= 0  →  { unit: 'started' }
 *
 * Pass `now` explicitly for deterministic testing.
 */
export function timeUntil(startIso: string, now: Date = new Date()): TimeLeft {
  const startMs = new Date(startIso).getTime()
  const diffMs = startMs - now.getTime()

  if (diffMs <= 0) return { unit: 'started' }

  const totalSeconds = Math.floor(diffMs / 1_000)
  const totalMinutes = Math.floor(diffMs / 60_000)
  const totalHours = Math.floor(diffMs / 3_600_000)

  if (totalHours >= 24) return { unit: 'days', value: Math.floor(totalHours / 24) }
  if (totalMinutes >= 60) return { unit: 'hours', value: totalHours }
  return { unit: 'minutes', value: totalMinutes > 0 ? totalMinutes : Math.floor(totalSeconds / 60) }
}

/**
 * Renders a {@link TimeLeft} as translated copy, e.g. "3 days left" / "Event has started". Used
 * by the organiser-facing participation-reminder modal (`ParticipationReminderModal`) to preview
 * the "time left" phrase before sending.
 *
 * <p>The event-reminder email itself (real send and its WYSIWYG preview) renders its own
 * server-side equivalent — see `MailingService#buildEventReminderVars` and the
 * `tpl_event_reminder_time_left_*` / `tpl_event_reminder_started_*` keys in {@code emails.json} —
 * so the two surfaces intentionally use separate i18n keys, not this one.
 */
export function formatTimeLeftLabel(
  timeLeft: TimeLeft,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  if (timeLeft.unit === 'started') return t('reminder_time_started')
  if (timeLeft.unit === 'days') return t('reminder_time_days', { count: timeLeft.value })
  if (timeLeft.unit === 'hours') return t('reminder_time_hours', { count: timeLeft.value })
  return t('reminder_time_minutes', { count: timeLeft.value })
}
